import { auth, db } from "./firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import {
  doc, setDoc, collection,
  getDocs, deleteDoc, writeBatch, serverTimestamp, onSnapshot,
} from "firebase/firestore";

// ── Auth ─────────────────────────────────────────────────────────────────────
export const fbSignIn    = (email, password) => signInWithEmailAndPassword(auth, email, password);
export const fbRegister  = (email, password) => createUserWithEmailAndPassword(auth, email, password);
export const fbSignOut   = () => signOut(auth);
export const onFbAuthChange = (cb) => onAuthStateChanged(auth, cb);

// ── Helpers ──────────────────────────────────────────────────────────────────
function storeDoc(storeId, colName, docId) {
  return doc(db, "stores", storeId, colName, String(docId));
}
function storeCol(storeId, colName) {
  return collection(db, "stores", storeId, colName);
}

// ── Single document sync / delete ────────────────────────────────────────────
export async function syncDoc(storeId, colName, data) {
  try {
    await setDoc(storeDoc(storeId, colName, data.id), { ...data, _updatedAt: serverTimestamp() }, { merge: true });
    return true;
  } catch (e) { console.warn("Firestore sync error:", e.message); return false; }
}

export async function deleteDocFs(storeId, colName, docId) {
  try {
    await deleteDoc(storeDoc(storeId, colName, docId));
    return true;
  } catch (e) { console.warn("Firestore delete error:", e.message); return false; }
}

// ── Bulk sync (upload entire collection) ─────────────────────────────────────
export async function bulkSync(storeId, colName, items) {
  if (!items || !items.length) return;
  const BATCH_SIZE = 400;
  for (let i = 0; i < items.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    items.slice(i, i + BATCH_SIZE).forEach(item =>
      batch.set(storeDoc(storeId, colName, item.id), { ...item, _updatedAt: serverTimestamp() }, { merge: true })
    );
    await batch.commit();
  }
}

// ── Load one collection ───────────────────────────────────────────────────────
export async function loadCol(storeId, colName) {
  try {
    const snap = await getDocs(storeCol(storeId, colName));
    return snap.docs.map(d => { const data = d.data(); delete data._updatedAt; return data; });
  } catch (e) { console.warn("Firestore load error:", e.message); return null; }
}

// ── Load all store data at once ───────────────────────────────────────────────
export async function loadAllStoreData(storeId) {
  const [sales, inventory, categories, expenses, purchases, customers] = await Promise.all([
    loadCol(storeId, "sales"),
    loadCol(storeId, "inventory"),
    loadCol(storeId, "categories"),
    loadCol(storeId, "expenses"),
    loadCol(storeId, "purchases"),
    loadCol(storeId, "customers"),
  ]);
  return { sales, inventory, categories, expenses, purchases, customers };
}

// ── Users (global — not per store) ───────────────────────────────────────────
export async function syncUser(user) {
  try {
    await setDoc(doc(db, "users", String(user.id)), { ...user, _updatedAt: serverTimestamp() }, { merge: true });
    return true;
  } catch (e) { console.warn("Firestore user sync error:", e.message); return false; }
}

export async function deleteUserFs(userId) {
  try {
    await deleteDoc(doc(db, "users", String(userId)));
    return true;
  } catch (e) { console.warn("Firestore user delete error:", e.message); return false; }
}

export async function loadUsers() {
  try {
    const snap = await getDocs(collection(db, "users"));
    return snap.docs.map(d => { const data = d.data(); delete data._updatedAt; return data; });
  } catch (e) { console.warn("Firestore loadUsers error:", e.message); return null; }
}

// ── Stores (global) ───────────────────────────────────────────────────────────
export async function syncStore(store) {
  try {
    await setDoc(doc(db, "storeList", String(store.id)), { ...store, _updatedAt: serverTimestamp() }, { merge: true });
    return true;
  } catch (e) { console.warn("Firestore store sync error:", e.message); return false; }
}

export async function deleteStoreFs(storeId) {
  try {
    await deleteDoc(doc(db, "storeList", String(storeId)));
    return true;
  } catch (e) { console.warn("Firestore store delete error:", e.message); return false; }
}

export async function loadStores() {
  try {
    const snap = await getDocs(collection(db, "storeList"));
    return snap.docs.map(d => { const data = d.data(); delete data._updatedAt; return data; });
  } catch (e) { console.warn("Firestore loadStores error:", e.message); return null; }
}

// ── Real-time store listener (handles startup pull + live updates) ─────────────
export function subscribeToStore(storeId, callbacks) {
  const colNames = ["sales", "inventory", "categories", "expenses", "purchases", "customers"];
  const unsubs = colNames.map(col =>
    onSnapshot(
      storeCol(storeId, col),
      snap => {
        const items = snap.docs.map(d => { const data = d.data(); delete data._updatedAt; return data; });
        callbacks[col]?.(items);
      },
      err => console.warn("Firestore listen error:", col, err.message)
    )
  );
  return () => unsubs.forEach(u => u());
}
